import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { FilterState } from '../types';

interface TagFilterBarProps {
  tags: string[];
  value: FilterState;
  onChange: (next: FilterState) => void;
}

function parseCsv(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

const TagFilterBar: React.FC<TagFilterBarProps> = ({ tags, value, onChange }) => {
  const [textSearch, setTextSearch] = useState(value.text_search);
  const [includeTagsText, setIncludeTagsText] = useState(value.include_tags.join(', '));
  const [excludeTagsText, setExcludeTagsText] = useState(value.exclude_tags.join(', '));
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    setTextSearch(value.text_search);
    setIncludeTagsText(value.include_tags.join(', '));
    setExcludeTagsText(value.exclude_tags.join(', '));
  }, [value]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange({ ...valueRef.current, text_search: textSearch });
    }, 250);
    return () => clearTimeout(timeout);
  }, [textSearch, onChange]);

  const tagHint = useMemo(() => tags.slice(0, 12).join(', '), [tags]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr 1fr 180px',
        gap: 8,
        marginBottom: 12,
      }}
    >
      <input
        type="text"
        placeholder="Search by name"
        value={textSearch}
        onChange={(e) => setTextSearch(e.target.value)}
        style={{ padding: 8, border: '1px solid #ddd', borderRadius: 6 }}
      />
      <input
        type="text"
        placeholder="Include tags (comma-separated)"
        value={includeTagsText}
        title={tagHint}
        onBlur={() => {
          onChange({ ...value, include_tags: parseCsv(includeTagsText) });
        }}
        onChange={(e) => setIncludeTagsText(e.target.value)}
        style={{ padding: 8, border: '1px solid #ddd', borderRadius: 6 }}
      />
      <input
        type="text"
        placeholder="Exclude tags (comma-separated)"
        value={excludeTagsText}
        title={tagHint}
        onBlur={() => {
          onChange({ ...value, exclude_tags: parseCsv(excludeTagsText) });
        }}
        onChange={(e) => setExcludeTagsText(e.target.value)}
        style={{ padding: 8, border: '1px solid #ddd', borderRadius: 6 }}
      />
      <select
        value={value.untagged}
        onChange={(e) => onChange({ ...value, untagged: e.target.value as FilterState['untagged'] })}
        style={{ padding: 8, border: '1px solid #ddd', borderRadius: 6 }}
      >
        <option value="all">Show all</option>
        <option value="tagged">Only tagged</option>
        <option value="untagged">Only untagged</option>
      </select>
    </div>
  );
};

export default TagFilterBar;
